import Sala from "../../../types/Sala";
import { ISimpleUser } from "../../config/Empleados";
import * as constantes from "../../../common/Constantes";
import { Autocomplete, Avatar, Button, CircularProgress, Dialog, DialogActions, DialogContent, Grid, IconButton, InputAdornment, MenuItem, TextField, Typography } from "@mui/material";
import { LocalizationProvider, DatePicker, TimePicker } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment, { Moment } from 'moment';
import { aDateOInvalida } from '../../../utils/FechaSelector';
import AddIcon from '@mui/icons-material/Add';
import { createTheme } from '@mui/material/styles';
import IPaciente from "../../../types/Paciente";
import Utilities from "../../../common/Utilities";

const locale = constantes.locale;

interface CitaDialogProps {
    open: boolean;
    loading: boolean;
    onSave: () => void;
    onUpdate: () => void;
    onCancel: () => void;
    pacientes: IPaciente[];
    isEdit: boolean;
    pacienteId: string;
    setPacienteId: (id: string) => void;
    errorPaciente: string;
    setErrorPaciente: (msg: string) => void;
    sanitarios: ISimpleUser[];
    especialistaId: number;
    setEspecialistaId: (id: number) => void;
    salas: Sala[];
    salaId: number;
    setSalaId: (id: number) => void;
    setModalPaciente: (open: boolean) => void;
    
    start: Date | null;
    setStart: (date: Date) => void;
    startTime: Date | null;
    onChangeStartTime: (date: Date) => void;
    endTime: Date | null;
    setEndTime: (date: Date) => void;
    repetirId: number;
    setRepetirId: (id: number) => void;
    terminarRepetir: Date | null;
    setTerminarRepetir: (date: Date) => void;
}

const CitaDialog = ({ 
    open,
    loading,
    onSave,
    onUpdate,
    onCancel,
    pacientes,
    isEdit,
    pacienteId,
    setPacienteId,
    errorPaciente,
    setErrorPaciente,
    sanitarios,
    especialistaId,
    setEspecialistaId,
    salas,
    salaId,
    setSalaId,
    setModalPaciente,

    start,
    setStart,
    startTime,
    onChangeStartTime,
    endTime,
    setEndTime,
    repetirId,
    setRepetirId,
    terminarRepetir,
    setTerminarRepetir

}: CitaDialogProps) => {

  const theme = createTheme();

  return (
      <Dialog open={open} maxWidth="lg" fullWidth onClose={onCancel}>

          <DialogContent>
              <Typography variant="h4" sx={{ display: 'inline-flex' }}>
                  <Avatar sx={{ margin: theme.spacing(1), backgroundColor: '#2e7d32' }}><AddIcon /></Avatar><div style={{ margin: '10px auto' }}>{isEdit ? 'Cambiar cita' : 'Nueva cita'}</div>
              </Typography>
              <Grid container spacing={1}>
                  <Grid item md={5} xs={12}>
                      <Autocomplete
                          disablePortal
                          autoComplete
                          id="pacientes"
                          options={pacientes}
                          value={pacientes.filter(p => p._id === pacienteId)[0] || null}
                          noOptionsText={'No se han encontrado pacientes'}
                          renderOption={(props, option) => { return (<li {...props} key={option._id}>{`${option.nombre} ${option.apellidos}`}</li>) }}
                          renderInput={(params) => <TextField {...params} autoFocus label="Paciente" value={pacienteId} variant="outlined" margin="dense" fullWidth required error={errorPaciente !== '' ? true : false} helperText={errorPaciente} InputProps={{ ...params.InputProps, startAdornment: (<InputAdornment position="start"><IconButton onClick={() => { setModalPaciente(true) }}><AddIcon titleAccess="Nuevo paciente" /></IconButton></InputAdornment>) }} />}
                          onChange={(event, value) => { value && setPacienteId(value._id); setErrorPaciente(''); }}
                          getOptionLabel={(option) => `${option.nombre} ${option.apellidos}` || ""}
                          filterOptions={(options, params) => Utilities.filterPacientesAutocomplete(options, params.inputValue)}
                      />
                  </Grid>
                  <Grid item md={2} xs={12}>
                      <TextField 
                          select value={especialistaId} 
                          variant="outlined" 
                          label="Especialista" 
                          margin="dense" 
                          fullWidth 
                          required 
                          onChange={(event) => {
                              const id = parseInt(event.target.value);
                              setEspecialistaId(id);
                              const especialista = sanitarios.find(s => s.internalID === id);
                              if (especialista && especialista.salaId !== null && especialista.salaId !== undefined) {
                                  setSalaId(especialista.salaId);
                              }
                          }} 
                      >
                          {sanitarios
                              .filter(({ activo, internalID }) => {
                                if (isEdit) {
                                  return activo || internalID === especialistaId; // Mostrar activos o seleccionado si está en edición
                                }
                                return activo; // Mostrar solo activos si es un alta
                              })
                              .map(({ internalID, firstName, activo }) => (
                                <MenuItem
                                  key={internalID}
                                  value={internalID}
                                  disabled={isEdit && internalID === especialistaId && !activo} // Deshabilitar si está seleccionado y no activo en edición
                                >
                                  {firstName}
                                </MenuItem>
                          ))}
                      </TextField>
                  </Grid>
                  <Grid item md={2} xs={12}>
                      <TextField select value={salaId} variant="outlined" label="Sala" margin="dense" fullWidth required onChange={(event) => setSalaId(parseInt(event.target.value))} >
                          {
                              salas.map((sala: any) => {
                                  return (
                                      <MenuItem key={sala.internalID} value={sala.internalID}>{sala.title}</MenuItem>
                                  );
                              })
                          }
                      </TextField>
                  </Grid>
                  <Grid item md={2} xs={12}>
                  </Grid>
                  <Grid item md={2} xs={12}>
                      <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale={locale}>
                          <DatePicker
                              renderInput={(props: any) => <TextField required fullWidth {...props} />}
                              label="Fecha"
                              toolbarFormat="dd-mm-yyyy"
                              value={start}
                              onChange={(date: Moment | null) => setStart(aDateOInvalida(date))}
                          />
                      </LocalizationProvider>
                  </Grid>
                  <Grid item md={2} xs={12}>
                      <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale={locale}>
                          <TimePicker
                              renderInput={(props: any) => <TextField required fullWidth {...props} />}
                              label="Inicio"
                              minutesStep={5}
                              value={startTime}
                              ampm={false}
                              onChange={(date: Moment | null) => onChangeStartTime(aDateOInvalida(date))}
                          />
                      </LocalizationProvider>
                  </Grid>
                  <Grid item md={2} xs={12}>
                      <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale={locale}>
                          <TimePicker
                              renderInput={(props: any) => <TextField required fullWidth {...props} />}
                              label="Final"
                              minutesStep={5}
                              value={endTime}
                              ampm={false}
                              onChange={(date: Moment | null) => setEndTime(aDateOInvalida(date))}
                          />
                      </LocalizationProvider>
                  </Grid>
                  <Grid item md={3} xs={12}>
                      <TextField disabled={isEdit} select value={repetirId} variant="outlined" label="Repetir" fullWidth onChange={(event) => setRepetirId(parseInt(event.target.value))}>
                          {
                              constantes.repetir.map((r, index) => {
                                  return (
                                      <MenuItem key={index} value={index}>{r}</MenuItem>
                                  );
                              })
                          }
                      </TextField>
                  </Grid>
                  {repetirId !== 0 && <Grid item md={3} xs={12}>
                      <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale={locale}>
                          <DatePicker
                              disabled={isEdit}
                              renderInput={(props: any) => <TextField fullWidth {...props} />}
                              label="Terminar repetición"
                              value={terminarRepetir}
                              onChange={(date: Moment | null) => {
                                const fecha = aDateOInvalida(date);
                                setTerminarRepetir(isNaN(fecha.getTime()) ? fecha : moment(fecha).endOf('day').toDate());
                              }}
                          />
                      </LocalizationProvider>
                  </Grid>}
              </Grid>
          </DialogContent>
          {loading ?
              <Grid container>
                  <CircularProgress style={{ margin: '20px auto' }} />
              </Grid> :
              <DialogActions>
                  <Button onClick={isEdit ? onUpdate : onSave}>Guardar</Button>
                  <Button onClick={onCancel}>Cancelar</Button>
              </DialogActions>
          }
      </Dialog>
    );
};

export default CitaDialog;